import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  Button,
  Container,
  IconButton,
  Divider,
  Tooltip,
  Checkbox,
  Collapse,
  Fade,
  CircularProgress,
} from "@mui/material";
import {
  DeleteOutline,
  InfoOutlined,
  ShoppingCartOutlined,
  Favorite,
} from "@mui/icons-material";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import configLocal from "../../config/config.local.json";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const ShopWishlist = () => {
  const BASE_URL = configLocal.baseURL;
  const navigate = useNavigate();
  const shopParam = "test.spadeplatform.com";
  const { refreshCart } = useCart();
  const [wishlist, setWishlist] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [removingItems, setRemovingItems] = useState([]);

  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const observer = useRef();
  const limit = 12;

  const brand = {
    navy: "#2E4053",
    silverBlue: "#D1D8E0",
    textLight: "#34495E",
    muted: "#A6AAAD",
    white: "#FFFFFF",
    danger: "#E74C3C",
  };
  /* ================= AUTH GUARD ================= */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to view your wishlist.");
      navigate("/shop/customerlogin");
    }
  }, [navigate]);
  /* ================= FETCH ================= */

  const fetchWishlist = async (pageNumber = 1) => {
    if (loading) return;

    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("You must log in to access the wishlist");
        navigate("/shop/customerlogin");
        return;
      }

      const params = {
        shop: shopParam, // dynamic shop
        page: pageNumber,
        limit: limit, // dynamic limit
        sortField: sortField, // dynamic sorting
        sortOrder: sortOrder,
        search: search || undefined, // optional search
      };

      const res = await axios.get(`${BASE_URL}/api/public/wishlist`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { messageId, data = [], pagination } = res.data;
      if (!res?.data) return;
      if (messageId === 1) {
        localStorage.removeItem("token");
        toast.error("Session expired. Please login again.");
        navigate("/shop/customerlogin");
        return;
      }
      if (res && res.data) {
        setWishlist((prev) => (pageNumber === 1 ? data : [...prev, ...data]));
      }

      setHasMore(pagination.currentPage < pagination.totalPages);
    } catch (err) {
      console.error("Error fetching wishlist:", err);

      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        toast.error("Session expired. Please login again.");
        navigate("/shop/customerlogin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shopParam) return;

    setWishlist([]);
    setHasMore(true);

    fetchWishlist(1);
  }, [shopParam, sortField, sortOrder, search]);

  /* ================= INFINITE SCROLL ================= */

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchWishlist(nextPage);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, page]
  );

  /* ================= SELECT ================= */

  const toggleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedItems.length === wishlist.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(wishlist.map((item) => item.id));
    }
  };

  /* ================= REMOVE ================= */

  // ✅ Fix — add token
  const removeItems = async (ids) => {
    try {
      setRemovingItems(ids);

      const token = localStorage.getItem("token"); // ✅ get token

      await Promise.all(
        ids.map((id) =>
          axios.post(
            `${BASE_URL}/api/public/wishlist-remove/${id}?shop=${shopParam}`,
            {}, // ✅ 2nd arg = body (empty)
            {
              headers: {
                Authorization: `Bearer ${token}`, // ✅ 3rd arg = config
              },
            }
          )
        )
      );

      setTimeout(() => {
        setWishlist((prev) => prev.filter((item) => !ids.includes(item.id)));
        setSelectedItems([]);
        setRemovingItems([]);
      }, 300);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= MOVE TO CART ================= */

  const moveToCart = async (ids) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found — user not logged in");
        return;
      }

      await Promise.all(
        ids.map((id) =>
          axios.post(
            `${BASE_URL}/api/public/wishlist-move-to-cart?shop=${shopParam}`,
            { product_id: id },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        )
      );

      // ✅ Remove from wishlist UI
      setWishlist((prev) => prev.filter((item) => !ids.includes(item.id)));
      setSelectedItems([]);

      // ✅ Tell CartContext to refresh
      refreshCart(); // call your cart context refresh function

      // ✅ Show success feedback
      toast.success("Item moved to cart!");

      // ❌ Don't call removeItems() — backend already removed from wishlist
    } catch (err) {
      console.error("moveToCart error:", err.response?.data || err.message);
      toast.error("Failed to move item to cart");
    }
  };

  /* ================= UI ================= */

  return (
    <Box sx={{ backgroundColor: "#F4F7F9", minHeight: "100vh" }}>
      <Container maxWidth="xxl" sx={{ py: 6 }}>
        {/* Header */}
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            pl: 3,
            borderLeft: 6,

            borderColor: "brand.silverBlue", // Referencing theme path directly
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "fontWeightBold", // Using theme-mapped tokens
              color: brand.navy,
            }}
          >
            {`My Wishlist`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {wishlist.length} items saved for later
          </Typography>
          {wishlist.length > 0 && (
            <Checkbox
              color="primary"
              checked={selectedItems.length === wishlist.length}
              onChange={selectAll}
              inputProps={{ "aria-label": "Select all wishlist items" }}
            />
          )}
        </Box>

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <Fade in>
            <Box
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 3,
                background: "#fff",
                boxShadow: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography fontWeight={700}>
                {selectedItems.length} selected
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartOutlined />}
                  onClick={async () => {
                    await moveToCart(selectedItems);
                    await refreshCart();
                  }}
                  sx={{ bgcolor: brand.navy }}
                >
                  Move to Cart
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => removeItems(selectedItems)}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {/* Grid */}
        <Grid container spacing={4}>
          {wishlist.map((product, index) => {
            const isLast = wishlist.length === index + 1;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={product.id}
                ref={isLast ? lastItemRef : null}
              >
                <Collapse
                  in={!removingItems.includes(product.id)}
                  timeout={300}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${brand.silverBlue}`,
                      position: "relative",
                    }}
                  >
                    <Checkbox
                      checked={selectedItems.includes(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        zIndex: 2,
                      }}
                    />

                    <IconButton
                      sx={{ position: "absolute", top: 10, right: 10 }}
                    >
                      <Favorite sx={{ color: brand.danger }} />
                    </IconButton>

                    <Box sx={{ position: "relative", pt: "100%" }}>
                      <CardMedia
                        component="img"
                        image={product.photo}
                        sx={{
                          position: "absolute",
                          top: 0,
                          height: "100%",
                          objectFit: "contain",
                          p: 2,
                        }}
                      />
                    </Box>

                    <CardContent>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/shopProductPage/${product.id}`)
                        }
                      >
                        {product.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: brand.textLight, mb: 2 }}
                      >
                        {product.description}
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <Typography fontWeight={900} sx={{ mb: 2 }}>
                        {product.selling_price} {product.currency || "EGP"}
                      </Typography>

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ShoppingCartOutlined />}
                        onClick={async () => {
                          await moveToCart([product.id]);
                          await refreshCart();
                        }}
                        sx={{ bgcolor: brand.navy }}
                      >
                        Move to Cart
                      </Button>
                    </CardContent>
                  </Card>
                </Collapse>
              </Grid>
            );
          })}
        </Grid>

        {/* Loading */}
        {loading && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* End State */}
        {!hasMore && !loading && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ color: brand.muted }}>
              You’ve reached the end of your wishlist.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ShopWishlist;
